/**
 * "People and access", "Reports" and "Settings and admin".
 */

import type { HelpArticle } from '../types';

export const peopleArticles: HelpArticle[] = [
  {
    slug: 'contacts',
    title: 'Contacts — the people you deal with',
    summary: 'Contacts are humans outside your firm. Your own colleagues are users, which is a different thing.',
    category: 'people',
    keywords: ['contact', 'people', 'person', 'client contact', 'phone', 'email', 'address book', 'who to call'],
    related: ['users-and-roles', 'the-big-picture'],
    body: [
      {
        kind: 'text',
        text: 'A contact is a person you deal with outside your firm — someone who works for a client, or someone at the assessor’s office.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Contacts are external only',
        text: 'Your own colleagues are not contacts. They are users, set up under User Management. If you are looking for a teammate and cannot find them in Contacts, that is why.',
      },
      {
        kind: 'text',
        text: 'A client-side contact is attached to the client and given a level, which describes their role in the relationship — the day-to-day person, the one who signs, the one who pays. A government-side contact is attached to the office they work at.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Record who you actually spoke to',
        text: 'When an assessor’s office agrees something on the phone, note the person’s name against the contact record. Six months later that is the difference between a fact and a vague memory.',
      },
    ],
  },

  {
    slug: 'users-and-roles',
    title: 'Users and roles',
    summary: 'Who can sign in, and what each role is allowed to do.',
    category: 'people',
    keywords: ['user', 'role', 'permission', 'access', 'admin', 'analyst', 'agent', 'super admin', 'cannot see', 'denied', 'why can i not', 'book'],
    related: ['contacts', 'permissions', 'the-client-portal'],
    body: [
      {
        kind: 'text',
        text: 'A user is anyone who can sign in. Your role decides what you see and what you can change.',
      },
      {
        kind: 'defs',
        intro: 'The roles inside the firm:',
        items: [
          { term: 'Super admin', meaning: 'Can do everything, including settings, users and permissions. Usually one or two people.' },
          { term: 'Admin', meaning: 'Broad access across the firm’s work, without the deepest system settings.' },
          { term: 'Administrator', meaning: 'An operations role covering the firm’s work but deliberately kept away from fee and money screens. The name is confusingly close to Admin — they are not the same, and this one sees less.' },
          { term: 'Agent', meaning: 'Works a defined book of clients. Sees their own book, not the whole firm.' },
          { term: 'Analyst', meaning: 'Does the supporting work on parcels and appeals across the firm.' },
        ],
      },
      {
        kind: 'defs',
        intro: 'Roles for people outside the firm:',
        items: [
          { term: 'Client', meaning: 'A client signing into the portal to see their own properties only.' },
          { term: 'Consultant', meaning: 'An outside partner with access limited to the work they are involved in.' },
          { term: 'Training', meaning: 'A view-only account for someone learning the system.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Why a colleague sees numbers you do not',
        text: 'If two people open the same dashboard and see different totals, it is almost always the book. An agent sees their own clients; an admin sees everyone. Neither screen is broken.',
      },
    ],
  },

  {
    slug: 'permissions',
    title: 'Permissions — when you cannot do something',
    summary: 'What to check, and what to ask for, when the app will not let you at something.',
    category: 'people',
    keywords: ['permission', 'access denied', 'cannot edit', 'cannot see', 'blocked', 'read only', 'grayed out', 'greyed out', 'missing menu', 'security group'],
    related: ['users-and-roles'],
    body: [
      {
        kind: 'text',
        text: 'On top of your role, an admin can grant or withhold access to each kind of record and each action on it — view, add, edit, delete.',
      },
      {
        kind: 'bullets',
        intro: 'What it looks like when you do not have access:',
        items: [
          'A menu item is simply not there.',
          'A button is missing or grayed out.',
          'You open a link somebody sent you and get told you do not have access.',
        ],
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Nothing is broken and you have not done anything wrong',
        text: 'Missing menu items are the normal, designed state for most users. If you need access, ask an admin and tell them the specific thing you were trying to do — that is much easier to grant than a vague request for more access.',
      },
    ],
  },

  {
    slug: 'the-client-portal',
    title: 'The client portal',
    summary: 'What your clients see when they sign in, so you can answer them without guessing.',
    category: 'people',
    keywords: ['portal', 'client portal', 'client login', 'what do clients see', 'external', 'share', 'client access', 'invite'],
    related: ['information-requests', 'documents', 'users-and-roles'],
    body: [
      {
        kind: 'text',
        text: 'Clients get their own stripped-back view. They see their properties, how their appeals are going, documents you have shared with them, and requests you have made for information.',
      },
      {
        kind: 'text',
        text: 'They cannot see other clients, your internal notes, or anything about your fees beyond their own invoices.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Some documents share themselves automatically',
        text: 'Certain document types are visible to the client as soon as they are uploaded with that type. Before uploading something sensitive, check the type you are giving it.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'To see exactly what a client sees',
        text: 'An admin can use "View as" in the top bar to look at the app through that client’s account. It is the reliable way to answer "I cannot find it on my screen".',
      },
    ],
  },
];

export const reportsArticles: HelpArticle[] = [
  {
    slug: 'reports-overview',
    title: 'Running a report',
    summary: 'Ready-made reports for the questions people ask most often.',
    category: 'reports',
    keywords: ['report', 'reports', 'run report', 'export', 'download', 'csv', 'summary', 'client report', 'savings report'],
    related: ['report-builder', 'saved-views', 'parent-clients'],
    body: [
      {
        kind: 'text',
        text: 'Click All Reports in the Reports section. Each card is a report somebody already built. Click one to run it.',
      },
      {
        kind: 'shot',
        src: '/help-shots/reports.png',
        alt: 'The reports index showing a grid of report cards, each with a title and a short description of what it produces.',
      },
      {
        kind: 'text',
        text: 'Some reports need you to pick something first, like a client or a year. Results can be exported so you can send them on.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Some reports are not on this list on purpose',
        text: 'A report that only makes sense for one particular parcel is launched from that parcel, not from here — running it without knowing which parcel you meant would produce nothing useful.',
      },
    ],
  },

  {
    slug: 'report-builder',
    title: 'Building your own report',
    summary: 'When no ready-made report answers your question, build one and save it.',
    category: 'reports',
    keywords: ['report builder', 'custom report', 'build report', 'my own report', 'columns', 'filters', 'query', 'template'],
    related: ['reports-overview', 'saved-views'],
    body: [
      {
        kind: 'steps',
        steps: [
          'Go to Report Builder in the Reports section.',
          'Choose what you are reporting on — parcels, appeals, invoices, and so on.',
          'Pick the columns you want to see.',
          'Add filters to narrow it down.',
          'Run it, check the result, then save it if it is useful.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Sanity-check the row count',
        text: 'Before trusting a new report, check the number of rows against something you already know. A report that returns four rows when you expected four hundred usually has one filter too many.',
      },
    ],
  },

  {
    slug: 'saved-views',
    title: 'Saved views — stop rebuilding the same filter',
    summary: 'Save a list you filter often, and get back to it in one click.',
    category: 'reports',
    keywords: ['saved view', 'save filter', 'my view', 'shortcut', 'bookmark', 'default view', 'list filter'],
    related: ['reports-overview', 'the-appeals-queue'],
    body: [
      {
        kind: 'text',
        text: 'Any list you can filter — appeals, parcels, invoices, documents — can have the filter saved.',
      },
      {
        kind: 'steps',
        steps: [
          'Filter and sort the list until it shows what you want.',
          'Open the views menu at the top of the list and save it.',
          'Give it a name that says what it is for, like "My TX protests due this month".',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        text: 'A view saves the filter, not the results. Open it tomorrow and it re-runs against today’s data.',
      },
    ],
  },
];

export const adminArticles: HelpArticle[] = [
  {
    slug: 'org-settings',
    title: 'Organization settings',
    summary: 'Firm-wide settings — the current tax year, branding, and defaults everything else inherits.',
    category: 'admin',
    keywords: ['org settings', 'organization', 'settings', 'branding', 'logo', 'colors', 'current year', 'defaults', 'firm settings', 'timezone'],
    related: ['year-roll', 'users-and-roles', 'custom-fields'],
    body: [
      {
        kind: 'text',
        text: 'Org Settings is for the whole firm, not for you personally. Changes here affect everybody, which is why it is limited to admins.',
      },
      {
        kind: 'bullets',
        intro: 'What lives here:',
        items: [
          'The current tax year, which new records default to.',
          'Your firm’s name, logo and colors, which appear across the app and on documents clients see.',
          'The time zone, which decides how dates and deadlines are displayed.',
          'Default fee settings for new clients.',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Changing the current year is not a filter',
        text: 'It changes what new records default to across the whole firm. If you only want to look at a different year, use the year selector on the list you are viewing instead.',
      },
    ],
  },

  {
    slug: 'year-roll',
    title: 'Year roll — moving the firm into a new tax year',
    summary: 'The once-a-season action that carries your book forward into the next year.',
    category: 'admin',
    keywords: ['year roll', 'roll', 'new year', 'next year', 'annual', 'rollover', 'season', 'carry forward'],
    related: ['the-tax-year', 'org-settings'],
    body: [
      {
        kind: 'text',
        text: 'At the start of a new tax season, your clients, properties and parcels need to exist for the new year. Year Roll does that in one operation instead of by hand.',
      },
      {
        kind: 'text',
        text: 'It reports what it did — how many records were carried forward and how many were skipped.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Read the skipped count, not just the success message',
        text: 'A roll that says it succeeded may still have skipped records — usually ones that were inactive or incomplete. Those parcels will quietly not exist for the new year, and nobody will notice until a deadline is missed. Always check what was skipped and why.',
      },
      {
        kind: 'callout',
        tone: 'note',
        text: 'Rolling forward does not delete anything. Last year stays exactly as it was.',
      },
    ],
  },

  {
    slug: 'custom-fields',
    title: 'Custom fields',
    summary: 'Adding your own fields when your firm tracks something the app does not have a box for.',
    category: 'admin',
    keywords: ['custom field', 'extra field', 'add field', 'own field', 'bespoke', 'configure', 'additional data'],
    related: ['org-settings', 'report-builder'],
    body: [
      {
        kind: 'text',
        text: 'If your firm tracks something the standard screens do not cover, an admin can add a custom field for it. Custom fields appear on the record type they were created for, and can be reported on.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Check there is not already a field for it',
        text: 'Custom fields multiply quickly, and two fields holding almost the same thing is worse than one imperfect field. Look before you add.',
      },
    ],
  },

  {
    slug: 'state-rules',
    title: 'State rules',
    summary: 'The deadlines and appeal rules for each state, which drive the warnings you get.',
    category: 'admin',
    keywords: ['state rules', 'state', 'rules', 'deadlines by state', 'calendar rules', 'jurisdiction rules', 'law', 'statute'],
    related: ['deadlines-and-hearings', 'the-calendar'],
    body: [
      {
        kind: 'text',
        text: 'Every state runs its property tax calendar differently. State Rules holds those differences — when notices go out, when protests are due, what the appeal path looks like.',
      },
      {
        kind: 'text',
        text: 'This is what lets the app tell you a deadline is coming. If a parcel is attached to the wrong state or the wrong assessor, the warnings will be wrong too.',
      },
      {
        kind: 'callout',
        tone: 'note',
        text: 'These are reference rules, not legal advice. When a lot of money depends on a date, confirm it with the jurisdiction.',
      },
    ],
  },

  {
    slug: 'notifications',
    title: 'Notifications',
    summary: 'The bell in the top bar, and how to make it tell you the right things.',
    category: 'admin',
    keywords: ['notification', 'bell', 'alert', 'email', 'reminder', 'too many emails', 'turn off', 'preferences', 'subscribe'],
    related: ['deadlines-and-hearings', 'notes-and-activity'],
    body: [
      {
        kind: 'text',
        text: 'The bell in the top bar collects things that need your attention — a deadline approaching, a document assigned to you, somebody mentioning you in a note.',
      },
      {
        kind: 'text',
        text: 'Click Notifications in the Operations section to see everything and to choose what you get told about, and whether it comes by email as well as in the app.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Turn things off rather than ignoring them',
        text: 'A bell that is always full stops being a signal. If a notification type is not useful to you, switch it off so the ones that matter still stand out.',
      },
    ],
  },

  {
    slug: 'activity-and-audit-logs',
    title: 'Activity log and audit log',
    summary: 'Two different histories: what has been happening, and a tamper-evident record of it.',
    category: 'admin',
    keywords: ['activity log', 'audit log', 'history', 'who did what', 'changes', 'track', 'compliance', 'trail', 'evidence'],
    related: ['notes-and-activity', 'users-and-roles'],
    body: [
      {
        kind: 'defs',
        items: [
          { term: 'Activity log', meaning: 'A readable feed of what has been happening across the firm — records created, values changed, appeals moved. This is the one you will use day to day.' },
          { term: 'Audit log', meaning: 'A stricter record kept for compliance, including sign-ins and permission changes. It is protected against being altered after the fact, so it can be relied on in a dispute.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'note',
        text: 'Neither log can be edited, by anyone, including admins. That is the point of them.',
      },
    ],
  },
];
